import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import {
  setFoodAllergensFilter,
  setFoodIngredientsFilter,
  setFoodPreferencesFilter,
} from '../../../../redux/me/me.actions';
import Lottie from 'lottie-react';
import { replaceColorInLottie } from '../../../../utility/util/util.service';
import drinkSelectionAnimationJson from '../../../../assets/lottie/drink-selection.json';
import foodSelectionAnimationJson from '../../../../assets/lottie/food-drink-selection.json';
import { Button, Col, Row } from 'reactstrap';
import classnames from 'classnames';
import { ORDER_TYPE_SELF_SERVICE } from '../../../../utility/constants';
import DropdownField from '../../form/fields/DropdownField';
import { debounce } from 'debounce';
import {
  removeCartItem,
  setCheckout,
  updateCartItem,
} from '../../../../redux/store/store.actions';
import { AvForm } from 'availity-reactstrap-validation';
import * as Icon from 'react-feather';
import { setIsSelfServiceTableSelectionModalOpen } from '../../../../redux/modal/modal.actions';

const SelfServiceTableSelectionModal = ({
  store,
  checkout,
  kiosk,
  tables,
  isOpen,
  cart,
  merchantProfile,
  items,
  showReducedModal,

  setCheckout,
  setIsSelfServiceTableSelectionModalOpen,
  updateCartItem,
  removeCartItem,
}) => {
  const intl = useIntl();
  const [selectedSelfServiceOption, setSelectedSelfServiceOption] = useState(
    null,
  );
  const [showTableDropdown, setShowTableDropdown] = useState(null);

  const drinkSelectionAnimation = useMemo(
    () => (
      <Lottie
        style={{ width: '100%', maxWidth: '250px' }}
        animationData={replaceColorInLottie(
          drinkSelectionAnimationJson,
          store?.primaryColor || '#22442c',
        )}
        loop={true}
      />
    ),
    [store?.primaryColor],
  );

  const foodSelectionAnimation = useMemo(
    () => (
      <Lottie
        style={{ width: '100%', maxWidth: '250px' }}
        animationData={replaceColorInLottie(
          foodSelectionAnimationJson,
          store?.primaryColor || '#22442c',
        )}
        loop={true}
      />
    ),
    [store?.primaryColor],
  );
  const includedCategories =
    merchantProfile?.selfServiceTableSelectionCategories || [];
  useEffect(() => {
    if (
      merchantProfile?.enableSelfServiceTableSelection &&
      checkout?.orderType === ORDER_TYPE_SELF_SERVICE &&
      !checkout?.tableId &&
      cart
    ) {
      const itemIdsAddedToCart = [
        ...new Set(
          cart
            ?.map((orderItem) => orderItem?.itemData?.item?.id)
            .filter(Boolean),
        ),
      ];

      // get all ItemCategory IDs, with Items from them added to cart
      const categoryIdsWithItemsInCart = itemIdsAddedToCart
        .flatMap((id) => items?.find((item) => id === item?.id)?.categories)
        .filter(Boolean)
        .map(({ id }) => id);

      // extract IDs from categories
      const selfServiceTableSelectionCategoryIds = (
        merchantProfile.selfServiceTableSelectionCategories || []
      ).map(({ id }) => id);

      // if categoryIdsWithItemsInCart and selfServiceTableSelectionCategoryIds overlap, show the Table Dropdown
      const showTableDropdownLocal = !!categoryIdsWithItemsInCart?.some((id) =>
        selfServiceTableSelectionCategoryIds.includes(id),
      );

      if (showTableDropdownLocal) {
        setShowTableDropdown(showTableDropdownLocal);
        setSelectedSelfServiceOption('2');
      }
    }
  }, [cart, merchantProfile]);

  const borderPrimaryDrinks = selectedSelfServiceOption === '1';
  const borderPrimaryFood = selectedSelfServiceOption === '2';

  const setCheckoutDebounced = debounce(setCheckout, 350);

  return (
    <ModalSheet
      isOpen={isOpen}
      closeModal={() => {
        if (showReducedModal) {
          setIsSelfServiceTableSelectionModalOpen(false);
          setSelectedSelfServiceOption(null);
        }
        console.log('you cant close this modal');
      }}
    >
      <div className="px-1">
        <h2 className="font-medium-3 text-center">
          <FormattedMessage
            id={
              showReducedModal
                ? 'changeTableTitle'
                : 'selfServiceTableSelectionModalTitle'
            }
          />
        </h2>
        {!showReducedModal && (
          <Row className="d-flex justify-content-between mt-2">
            <Col
              onClick={() => {
                setSelectedSelfServiceOption('2');
              }}
              xs={6}
            >
              <div className="h-100">
                <div
                  className={classnames('rounded h-100', {
                    'border-primary': borderPrimaryFood,
                    'border dashed': !borderPrimaryFood,
                  })}
                >
                  <p className="mt-1 text-center">
                    <FormattedMessage id="selfServiceFoodText" />
                  </p>
                  <div className="d-flex h-100">{foodSelectionAnimation}</div>
                </div>
              </div>
            </Col>
            <Col
              onClick={() => {
                setSelectedSelfServiceOption('1');
                if (showTableDropdown) {
                  // Remove the () after cart[cart.length - 1] since it's not a function
                  const { hash, itemData, additions, qty } =
                    cart?.[cart.length - 1] || {};
                  removeCartItem(cart, hash);
                }
                setCheckoutDebounced({ tableId: null });
                setIsSelfServiceTableSelectionModalOpen(false);
              }}
              xs={6}
            >
              <div>
                <div
                  className={classnames('rounded', {
                    'border-primary': borderPrimaryDrinks,
                    'border dashed': !borderPrimaryDrinks,
                  })}
                >
                  <p className="mt-1 text-center">
                    <FormattedMessage id="selfServiceDrinksText" />
                  </p>
                  {drinkSelectionAnimation}
                </div>
              </div>
            </Col>
          </Row>
        )}
        <AvForm
          onSubmit={(event, errors) => {
            if (errors?.length === 0) {
              setIsSelfServiceTableSelectionModalOpen(false);
              setSelectedSelfServiceOption(null);
            }
          }}
        >
          {((selectedSelfServiceOption === '2' &&
            checkout?.orderType === ORDER_TYPE_SELF_SERVICE &&
            kiosk) ||
            showReducedModal) && (
            <>
              <DropdownField
                className={classnames('mt-3', {
                  'mt-5': showReducedModal,
                })}
                required
                isClearable={true}
                isMulti={false}
                options={tables?.sort((a, b) =>
                  a.name?.localeCompare(b.name, undefined, { numeric: true }),
                )}
                propertyName="tableId"
                labelMessageId="askForTableLabel"
                getOptionValue={(table) => table?.id}
                emptyValue={null}
                getOptionLabel={(table) => table?.name}
                onChange={(table) => {
                  setCheckoutDebounced({ tableId: table?.id });
                }}
                isKiosk={kiosk}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
                object={checkout}
                isSearchable={!kiosk}
              />
              <Button color="primary" className="w-100">
                <FormattedMessage id="confirm" />
              </Button>
            </>
          )}
        </AvForm>
      </div>
    </ModalSheet>
  );
};

const mapStateToProps = (state) => ({
  store: state.store.store,
  checkout: state.store.checkout,
  kiosk: state.store.kiosk,
  tables: state.store.tables,
  isOpen: state.modals.isSelfServiceTableSelectionModalOpen,
  cart: state.store.cart,
  merchantProfile: state.store.store?.merchantProfile,
  items: state.store.items,
  showReducedModal:
    state.modals.selfServiceTableSelectionModalData?.showReducedModal,
});

export default connect(mapStateToProps, {
  setIsSelfServiceTableSelectionModalOpen,
  setFoodAllergensFilter,
  setFoodPreferencesFilter,
  setFoodIngredientsFilter,
  setCheckout,
  updateCartItem,
  removeCartItem,
})(SelfServiceTableSelectionModal);
